import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import selectorProps from './selector-style';
import Contributor from './contributor';
import useGetContributorsByName from '../../../../../services/contributors/search-service';
import { useUpdateContributor } from '../../../../../services/wire/edit-services';
import useEditorStore from '../../state-hook';
import useContributorStore from './state-hook';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 5;
`;

const Anchor = styled.a`
  align-self: flex-end;
  display: inline-block;
  font: ${props => props.theme.fontWeightBold} 14px/120% ${props => props.theme.fontFamilyBody};
  margin-bottom: 8px;
  margin-right: 3px;
  color: ${props => props.theme.secondaryColor};
  text-transform: uppercase;
`;

export default function Selector() {
  // eslint-disable-next-line no-unused-vars
  const [{ data }, search] = useGetContributorsByName();
  const { callback: debouncedSearch } = useDebouncedCallback(search, 300, { leading: true });
  const [options, setOptions] = React.useState([]);
  const mounted = React.useRef(false);
  const selectRef = React.useRef();
  const valueChanged = React.useRef(false);
  const prev = useContributorStore(state => state.prev);
  const dft = useContributorStore(state => state.dft);
  const setContributor = useContributorStore(state => state.set);
  const setEditor = useEditorStore(state => state.set);
  const wire = useEditorStore(state => state.wire);
  const [{ response: updateResponse }, update] = useUpdateContributor(wire.id);

  React.useEffect(() => {
    mounted.current = true;
    _.get(selectRef, 'current') && selectRef.current.focus();
    return () => (mounted.current = false);
  }, []);

  React.useEffect(() => {
    const contributors = _.get(data, 'contributors', []);
    setOptions(contributors.map(c => ({
      ...c,
      label: `${c.name} ${c.organization}`,
      value: c.slug,
      isDefault: c.slug === dft,
    })));
  }, [data, dft]);

  React.useEffect(() => {
    if (_.get(updateResponse, 'status') === 200) {
      const profile = (_.get(updateResponse, 'data.profile', null));
      if (profile) {
        setContributor(state => {
          state.toChange = false;
        });
        setEditor(state => {
          state.wire.profile = profile;
        });
      }
    }
  }, [updateResponse, setContributor, setEditor]);

  const undo = e => {
    e && e.preventDefault();
    if (mounted.current && prev) {
      setContributor(state => {
        state.toChange = false;
      });
    }
  };

  const onInputChange = v => {
    if (mounted.current) {
      if (v.length >= 3) {
        debouncedSearch(v);
      } else {
        setOptions([]);
      }
    }
  };

  const onChange = v => {
    valueChanged.current = true;
    if (mounted.current) {
      if (v.slug === prev) {
        undo();
      } else {
        update(wire.id, v.slug);
      }
    }
  };

  const onMenuClose = () => {
    // This event is always happening before onChange.
    setTimeout(() => (mounted.current && valueChanged.current || undo()), 50);
  };

  const isSelected = d => prev === d.slug;

  return (
    <Container>
      <Anchor href="#" onClick={undo}>undo</Anchor>
      <Select
        {...selectorProps}
        ref={selectRef}
        options={options}
        className="contributors-select--container"
        onInputChange={onInputChange}
        onChange={onChange}
        onMenuClose={onMenuClose}
        formatOptionLabel={Contributor}
        isOptionSelected={isSelected}
      />
    </Container>
  );
}
